import debug from 'debug';

// GQL
import { client } from '../client';
import { ACTIONS_BY_IDS_WARMUP } from '../queries/actions/actions';

const log = debug('rois:gql:selectors:warmup');

// Límite del contrato backend: `actionsByIds` acepta máx 200 ids por llamada.
// Los detalles de OT rondan las decenas de hijas, el troceo es defensivo.
const BATCH_SIZE = 200;

/**
 * Warm-up de la caché normalizada de Apollo para las Actions de las hijas
 * del detalle de OT/campaña (y del RV en child-orders).
 *
 * Resuelve las Actions de todas las hijas en UNA sola query `actionsByIds`
 * (fragment NewAction, el MISMO de GET_ACTION_BY_ID — el batch backend
 * proyecta un superset del type, y `actionBudgetItemIDs` resuelve vía
 * DataLoader sobre el batch; verificado en la integración de rois-back).
 * Las entidades `Action:<id>` quedan normalizadas en caché, de modo que los
 * `client.query(GET_ACTION_BY_ID, cache-first)` per-row que vienen después
 * se sirven de caché vía el redirect `Query.action → toReference` de
 * policies.ts, en lugar de disparar A requests fila a fila (el N+1
 * getActionById×15 restante tras el Tier A del detalle).
 *
 * REGLA DE ORO — es una OPTIMIZACIÓN, jamás un gate: si falla (tablet
 * offline en un túnel, backend viejo sin la query `actionsByIds`, error
 * GraphQL, lo que sea) se ignora en silencio y el selector sigue
 * EXACTAMENTE como hoy — los per-row GET_ACTION_BY_ID intactos son el
 * fallback natural. Este helper NUNCA lanza.
 *
 * `fetchPolicy` (default `cache-first`, contrato de tablas): los DETALLES
 * (sin polling ni flag reFetch que refresque después) pasan `network-only`
 * para que CADA carga baje las Actions frescas en un solo round-trip —
 * mismo razonamiento que `warmupOrdersByIds`.
 */
export async function warmupActionsByIds(
  ids: (string | undefined | null)[],
  reFetch = false,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<void> {
  // Con reFetch=true el per-row va network-only e IGNORA la caché: el
  // warm-up solo añadiría una request extra con los mismos payloads que
  // las respuestas per-row ya traen. Solo aporta en el camino cache-first.
  if (reFetch) return;
  try {
    const uniqueIds = [
      ...new Set(ids.filter((id): id is string => Boolean(id))),
    ];
    if (uniqueIds.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
    }

    await Promise.all(
      batches.map((batch) =>
        client.query({
          query: ACTIONS_BY_IDS_WARMUP,
          variables: { ids: batch },
          fetchPolicy,
          // 'all' cubre "backend desplegado sin actionsByIds" (campo
          // desconocido → error GraphQL → se ignora, no revienta).
          errorPolicy: 'all',
        })
      )
    );
  } catch (err) {
    // VACÍO de efectos a propósito: warm-up fallido = comportamiento de hoy.
    log('warm-up actionsByIds omitido (sin efectos):', err);
  }
}
